import { memo, useCallback } from "react";

// UI
import { DatePicker } from "ui/components/DatePicker";
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";
import { ContactDropdownTypeEnum } from "ui/components/Dropdown/ContactDropdown/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { NumberField } from "ui/components/TextField/NumberField";
import { TextArea } from "ui/components/TextField/TextArea";
import { TextField } from "ui/components/TextField/TextField";
import VehicleLocationFields from "ui/modules/fleet/vehicles/pages/components/VehicleLocationFields/VehicleLocationFields";

import { ServiceBasicInfoFormProps } from "./types";

/** Serviseri su kontakti iz grupe Auto servis, ne svi dobavljači. */
const SERVICE_CONTACT_GROUP_IDS = ["A"];

/**
 * Prvi korak servisnog naloga: kod koga je vozilo, kada i zbog čega. Raspored prati
 * korake čarobnjaka premještaja — naslov odjeljka lijevo, objašnjenje desno.
 */
const ServiceBasicInfoForm: React.FC<ServiceBasicInfoFormProps> = (props) => {
  const { values, errors, onChange, location, onLocationChange } = props;

  const handleContactChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onChange({ ...values, contactId: item.value || null });
    },
    [values, onChange]
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      onChange({ ...values, [name]: value });
    },
    [values, onChange]
  );

  const handleDateChange = useCallback(
    (name: string, value: Date | null) => {
      onChange({ ...values, [name]: value });
    },
    [values, onChange]
  );

  const handleNumberChange = useCallback(
    (value: number | null, name?: string) => {
      if (!name) return;
      onChange({ ...values, [name]: value });
    },
    [values, onChange]
  );

  return (
    <div className="flex items-start justify-center px-4 pt-12 pb-6">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1mb-2">
          Servisni nalog
        </h1>
        <p className="text-center text-md text-slate-500 mb-8">
          Zabilježite kod koga je vozilo, kada je zaprimljeno i što se radi.
        </p>

        <div className="border bg-white border-slate-200">
          <div className="border-b border-slate-200 py-6 px-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                Servis
              </h3>
              <p className="text-xs text-slate-500">
                Kod koga je vozilo i pod kojim nazivom pratimo zahvat
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <ContactDropdown
                  name="contactId"
                  label="Servis"
                  placeholder="Odaberite servis"
                  value={values.contactId}
                  onChange={handleContactChange}
                  contactGroupIds={SERVICE_CONTACT_GROUP_IDS}
                  contactType={ContactDropdownTypeEnum.COMPANY}
                  error={errors.contactId}
                  required
                  width="full"
                />
              </div>
              <div>
                <NumberField
                  name="kmTraveled"
                  label="Kilometraža"
                  placeholder="Kilometraža"
                  value={values.kmTraveled}
                  onChange={handleNumberChange}
                  error={errors.kmTraveled}
                  decimals={0}
                  groupAppend="km"
                />
              </div>
              <div className="col-span-3">
                <TextField
                  type="text"
                  name="name"
                  label="Naziv naloga"
                  placeholder="npr. Redovni servis 60.000 km"
                  value={values.name}
                  onChange={handleTextChange}
                  error={errors.name}
                  required
                  width="full"
                />
              </div>
            </div>
          </div>

          <div className="border-b border-slate-200 py-6 px-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                Datumi
              </h3>
              <p className="text-xs text-slate-500">
                Datum preuzimanja se upisuje kad se vozilo vrati
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <DatePicker
                  name="receivedDate"
                  label="Datum zaprimanja"
                  placeholder="Datum zaprimanja"
                  value={values.receivedDate}
                  onChange={handleDateChange}
                  error={errors.receivedDate}
                  required
                  width="full"
                />
              </div>
              <div>
                <DatePicker
                  name="expectedDate"
                  label="Očekivani povratak"
                  placeholder="Očekivani povratak"
                  value={values.expectedDate}
                  onChange={handleDateChange}
                  error={errors.expectedDate}
                  width="full"
                />
              </div>
              <div>
                <DatePicker
                  name="completedDate"
                  label="Datum preuzimanja"
                  placeholder="Datum preuzimanja"
                  value={values.completedDate}
                  onChange={handleDateChange}
                  error={errors.completedDate}
                  width="full"
                />
              </div>
            </div>
          </div>

          {/* Lokacija se nudi samo kad nalog prati odlazak vozila na servis. */}
          {location && onLocationChange && (
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                  Lokacija vozila
                </h3>
                <p className="text-xs text-slate-500">
                  Predložen je odabrani servis — promijenite ako vozilo stoji drugdje
                </p>
              </div>
              <VehicleLocationFields
                values={location}
                errors={errors}
                onChange={onLocationChange}
                layout="grid"
                showValidFrom={false}
                showNote={false}
              />
            </div>
          )}

          {/* Opis je zadnji i najširi — piše se u par rečenica, ne u jedan redak. */}
          <div className="py-6 px-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                Opis zahvata
              </h3>
              <p className="text-xs text-slate-500">
                Što se radi na vozilu, ako treba zabilježiti
              </p>
            </div>
            <TextArea
              name="description"
              placeholder="Opis zahvata"
              value={values.description}
              onChange={handleTextChange}
              error={errors.description}
              rows={4}
              size="full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ServiceBasicInfoForm);
